/**
 * Pure SSE (Server-Sent Events) client for the help-assistant chat stream (AB#26561). Ported
 * verbatim (behaviour, not syntax) from the React reference implementation so the Vue and React
 * widgets parse and dispatch the same wire protocol:
 *
 * - createSseParser, streamHelpChat: sseClient.ts
 *   (syrenis-frontends/packages/syrenis-ui/src/components/HelpAssistant/sseClient.ts)
 *
 * Differences from the TS original (AB#26561 Task 4):
 * - streamHelpChat takes a single options object instead of positional arguments.
 * - `fetchImpl` is injectable (defaults to the global `fetch`) so `node --test` can stub it
 *   without a DOM/browser fetch implementation.
 * - No `credentials: 'include'`: the admin portal's bearer token rides the caller-supplied
 *   `authorization` header, not a same-origin session cookie.
 *
 * See docs/work/AB26561-chat-docs-help-poc/chat-core-contract.md for the wire contract (SSE
 * events, REST DTOs, error-message maps, client rules) this module assumes.
 */

/**
 * @typedef {object} HelpPersistedIds
 * @property {string} chatId
 * @property {number} userMessageId
 * @property {number} assistantMessageId
 */

/**
 * @typedef {object} HelpToolActivity
 * @property {string} name
 * @property {'started'|'finished'} status
 */

/**
 * A write action the assistant proposed and is holding until the user confirms it (agentic
 * reclassification, AB#26561 Task 3). Action tools are never auto-run: the server emits this so
 * the UI can render a confirm/reject card, and the real write only happens when the user confirms
 * via POST /api/help/actions/{actionId}/confirm. `args` is the proposed tool arguments as a parsed
 * object (whatever shape the tool takes), carried through for display/debugging.
 * @typedef {object} HelpProposedAction
 * @property {string} actionId
 * @property {string} toolName
 * @property {string} summary
 * @property {unknown} args
 */

/**
 * @typedef {object} HelpStreamHandlers
 * @property {(text: string) => void} onDelta
 * @property {(sources: string[]) => void} onSources
 * @property {(message: string) => void} onError
 * @property {(ids: HelpPersistedIds) => void} [onPersisted] Fired when the server persisted the completed turn (chat-persistence mode only).
 * @property {(activity: HelpToolActivity) => void} [onTool] Fired when the engine starts/finishes a tool call (agentic tool-use only).
 * @property {(action: HelpProposedAction) => void} [onAction] Fired when the assistant proposes a write action awaiting the user's confirmation.
 */

const GENERIC_ERROR = 'Something went wrong. Please try again.'

/**
 * Messages for the machine-readable error codes the server actually returns (AB#26561 review fix).
 * Every non-2xx used to collapse into GENERIC_ERROR without reading the body, which told the user to
 * "try again" in the two cases where retrying is exactly the wrong move: the tenant's
 * EnableHelpAssistant key being off (retry never succeeds) and a 429 (retry makes it worse).
 * @type {Record<string, string>}
 */
const ERROR_BY_CODE = {
	help_assistant_disabled:
		'The help assistant is not enabled for your organization. Contact your administrator if you think it should be.',
	forbidden: 'Your session cannot be used for the help assistant. Please sign out and back in.'
}

/** @type {Record<number, string>} */
const ERROR_BY_STATUS = {
	401: 'Your session has expired. Please sign in again.',
	429: 'You\'ve sent a lot of messages in a short time. Please wait a moment and try again.',
	502: 'The help assistant is temporarily unreachable. Please try again shortly.',
	503: 'The help assistant is temporarily unavailable. Please try again shortly.',
	504: 'That took too long to complete. Please try again.'
}

/**
 * Turns a failed response into the most specific message available: the server's own error code
 * first, then a status-based fallback, then the generic message. Reading the body is best-effort,
 * so a non-JSON or empty body degrades to the status/generic path rather than throwing.
 * @param {Response} response
 * @returns {Promise<string>}
 */
async function messageForFailedResponse (response) {
	try {
		const body = await response.clone().json()
		const code = typeof body?.error === 'string' ? body.error : undefined
		if (code && ERROR_BY_CODE[code]) return ERROR_BY_CODE[code]
	} catch {
		// Not JSON, or no body: fall through to the status-based message.
	}

	return ERROR_BY_STATUS[response.status] ?? GENERIC_ERROR
}

/**
 * A pure buffering SSE frame parser: feed it raw chunks of response text via `push`, and it calls
 * `onEvent(event, payload)` once per well-formed `event: ..` + `data: ..` frame (frames are
 * separated by a blank line, matching the SSE wire format). Heartbeat comment lines
 * (`: keep-alive`) and any frame missing an `event:` or `data:` line don't match the regexes and
 * are silently dropped; a `data:` line that isn't valid JSON is also silently dropped. Frames may
 * be split across multiple `push` calls: incomplete data is buffered until a terminating blank
 * line arrives. Filtering by event name (e.g. ignoring an event type the caller doesn't
 * recognise) is the caller's job, same as the if/else dispatch chain in the TS reference.
 * @param {(event: string, payload: unknown) => void} onEvent
 * @returns {{ push: (text: string) => void }}
 */
export function createSseParser (onEvent) {
	let buffer = ''
	return {
		push (text) {
			buffer += text
			let sep
			while ((sep = buffer.indexOf('\n\n')) !== -1) {
				const raw = buffer.slice(0, sep)
				buffer = buffer.slice(sep + 2)
				const event = /^event: (.+)$/m.exec(raw)?.[1]
				const data = /^data: (.+)$/m.exec(raw)?.[1]
				if (!event || !data) continue
				try {
					onEvent(event, JSON.parse(data))
				} catch {
					// Malformed JSON: skip this frame.
				}
			}
		}
	}
}

/**
 * POSTs the conversation and dispatches parsed SSE events via `createSseParser`. EventSource
 * cannot POST, hence manual parsing.
 * @param {object} options
 * @param {string} options.endpoint
 * @param {{ role: 'user'|'assistant', content: string }[]} options.messages
 * @param {HelpStreamHandlers} options.handlers
 * @param {AbortSignal} [options.signal]
 * @param {Record<string, string>} [options.headers]
 * @param {string} [options.context]
 * @param {string} [options.chatId]
 * @param {typeof fetch} [options.fetchImpl] Injectable so tests can stub the network call;
 *   defaults to the global `fetch`.
 * @returns {Promise<void>}
 */
export async function streamHelpChat ({ endpoint, messages, handlers, signal, headers, context, chatId, fetchImpl = fetch }) {
	let response
	try {
		response = await fetchImpl(endpoint, {
			method: 'POST',
			headers: { 'content-type': 'application/json', ...headers },
			body: JSON.stringify({
				messages,
				...(context ? { context } : {}),
				...(chatId ? { chatId } : {})
			}),
			signal
		})
	} catch (e) {
		if (e.name !== 'AbortError') handlers.onError(GENERIC_ERROR)
		return
	}
	if (!response.ok) {
		handlers.onError(await messageForFailedResponse(response))
		return
	}
	if (!response.body) {
		handlers.onError(GENERIC_ERROR)
		return
	}

	const reader = response.body.getReader()
	const decoder = new TextDecoder()
	const parser = createSseParser((event, payload) => {
		if (event === 'delta') handlers.onDelta(payload.text ?? '')
		else if (event === 'sources') handlers.onSources(payload.sources ?? [])
		else if (event === 'error') handlers.onError(payload.message ?? GENERIC_ERROR)
		else if (event === 'tool') handlers.onTool?.({ name: payload.name, status: payload.status })
		else if (event === 'action') {
			handlers.onAction?.({
				actionId: payload.actionId,
				toolName: payload.toolName,
				summary: payload.summary,
				args: payload.args
			})
		} else if (event === 'persisted') {
			handlers.onPersisted?.({
				chatId: payload.chatId,
				userMessageId: payload.userMessageId,
				assistantMessageId: payload.assistantMessageId
			})
		}
	})

	try {
		for (;;) {
			const { done, value } = await reader.read()
			if (done) break
			parser.push(decoder.decode(value, { stream: true }))
		}
		// The decoder buffers an incomplete multi-byte UTF-8 sequence across reads (that's what
		// `{ stream: true }` is for); flushing it here hands the parser whatever trailing bytes
		// the final chunk left buffered, so a codepoint split across the last chunk boundary
		// isn't silently dropped.
		const tail = decoder.decode()
		if (tail) parser.push(tail)
	} catch (e) {
		// Mid-stream failure (e.g. connection dropped, Bedrock throttling propagated as a
		// reader rejection). AbortError means the client cancelled deliberately - stop
		// silently rather than surfacing it as an error.
		if (e.name !== 'AbortError') handlers.onError(GENERIC_ERROR)
	}
}
